import type { SizeGuidePatch } from '../types';
import { useState } from 'react';
import { Banner, BlockStack, Card, DropZone, Text } from '@shopify/polaris';
import { useLanguage } from '../providers/LanguageProvider';
import type { SizeCoverageReport, SizeGuideEntryView } from '../types';
import type { SizeGuideBusyState, SizeGuideErrorState } from '../hooks/useSettings';
import { MAX_SIZE_GUIDES, sizeGuideErrorMessage, SizeGuideCard } from './SizeGuideCard';

/** Mirrors ALLOWED_SIZE_GUIDE_MIME_TYPES in web/backend/src/routes/settings.ts. */
const ACCEPTED_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'application/pdf'];

/** Mirrors SIZE_GUIDE_MAX_BYTES in web/backend/src/routes/settings.ts. */
const MAX_BYTES = 10 * 1024 * 1024;

export interface SizeGuidesSectionProps {
  guides: SizeGuideEntryView[];
  coverageReport: SizeCoverageReport | null;
  onCreate: (file: File, name?: string) => void;
  onPatch: (id: string, patch: SizeGuidePatch) => void;
  onRefresh?: (id: string) => Promise<void>;
  onDelete: (id: string) => void;
  busy: SizeGuideBusyState | null;
  error: SizeGuideErrorState | null;
  /** No create, no patch, no delete. Set when the settings never loaded: the
   * request would still reach the server, but nothing here could show the
   * result back. */
  disabled?: boolean;
  productTypeSuggestions: string[];
}

export function SizeGuidesSection({
  guides,
  coverageReport,
  onCreate,
  onPatch,
  onRefresh,
  onDelete,
  busy,
  error,
  disabled = false,
  productTypeSuggestions,
}: SizeGuidesSectionProps) {
  const { t, language } = useLanguage();
  const s = t.settings;

  const [localError, setLocalError] = useState<string | null>(null);

  const atCap = guides.length >= MAX_SIZE_GUIDES;
  const creating = busy?.op === 'create';

  // The check that follows a create/patch/delete runs fire-and-forget on the
  // backend (routes/settings.ts's refreshShopSizeCoverage), so the numbers
  // below can lag the assignment the merchant is currently looking at.
  // useSettings polls for a fresher report after a mutation, but that is a
  // best effort with a bounded window, not a guarantee — this is the honest
  // fallback for whatever it has not caught up with yet: not hiding the
  // staleness, naming when the data actually comes from.
  const checkedAtDate = coverageReport?.checkedAt ? new Date(coverageReport.checkedAt) : null;
  const checkedAtLabel =
    checkedAtDate && !Number.isNaN(checkedAtDate.getTime())
      ? s.sizeCoverageCheckedAt(checkedAtDate.toLocaleDateString(language === 'it' ? 'it-IT' : 'en-US'))
      : null;

  // The backend refuses both of these too. Catching them here saves a 10 MB
  // round trip and an error the merchant would read a few seconds later.
  const handleDrop = (files: File[]) => {
    const file = files[0];
    if (!file) return;
    if (!ACCEPTED_MIME_TYPES.includes(file.type)) {
      setLocalError(s.sizeGuideErrorUnsupportedType);
      return;
    }
    if (file.size > MAX_BYTES) {
      setLocalError(s.sizeGuideErrorTooLarge);
      return;
    }
    setLocalError(null);
    onCreate(file);
  };

  const handlePatch = (id: string, patch: SizeGuidePatch) => {
    setLocalError(null);
    onPatch(id, patch);
  };

  const handleDelete = (id: string) => {
    setLocalError(null);
    onDelete(id);
  };

  // A create has no guide yet, which is exactly how the hook marks a
  // create-time error (`id` undefined) — see SizeGuideErrorState's doc
  // comment in useSettings.ts. No local bookkeeping needed to tell it apart
  // from a patch/delete error any more: the hook now tags every error with
  // the guide it happened to, correlated against whichever request was
  // actually still current when the response came back, not whichever
  // request happened to be initiated most recently on this side.
  const sectionError = localError ?? (error && error.id === undefined ? sizeGuideErrorMessage(s, error) : null);

  return (
    <BlockStack gap="400">
      <Card>
        <BlockStack gap="400">
          <BlockStack gap="100">
            <Text as="h2" variant="headingMd">
              {s.sizeGuidesTitle}
            </Text>
            <Text as="p" tone="subdued">
              {s.sizeGuidesDescription}
            </Text>
          </BlockStack>

          {sectionError && (
            <Banner tone="critical">
              <p>{sectionError}</p>
            </Banner>
          )}

          <DropZone
            label={s.sizeGuidesAddAction}
            accept={ACCEPTED_MIME_TYPES.join(',')}
            type="file"
            allowMultiple={false}
            onDrop={handleDrop}
            disabled={disabled || atCap || creating}
          >
            <DropZone.FileUpload
              actionTitle={creating ? s.sizeGuideUploading : s.sizeGuideDropAction}
              actionHint={s.sizeGuideDropHint}
            />
          </DropZone>

          <Text as="p" variant="bodySm" tone="subdued">
            {atCap ? s.sizeGuideErrorTooMany(MAX_SIZE_GUIDES) : s.sizeGuideFallbackHint}
          </Text>
          {checkedAtLabel && (
            <Text as="p" variant="bodySm" tone="subdued">
              {checkedAtLabel}
            </Text>
          )}
        </BlockStack>
      </Card>

      {guides.map((entry) => (
        <SizeGuideCard
          key={entry.id}
          entry={entry}
          coverage={coverageReport?.guides.find((g) => g.id === entry.id)}
          coverageError={coverageReport?.error ?? null}
          productTypeSuggestions={productTypeSuggestions}
          onPatch={handlePatch}
          onRefresh={onRefresh}
          onDelete={handleDelete}
          // A create's busy/error state has no `id`, which can never equal
          // a real entry's id, so these already exclude create in-flight
          // and create failures without a separate check for it.
          busy={busy?.id === entry.id}
          errorCode={error?.id === entry.id ? error : null}
          disabled={disabled}
        />
      ))}
    </BlockStack>
  );
}

export default SizeGuidesSection;
